import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Certificate,
  DeclaredCertificate,
  DeviceCatalogEntry,
  DeviceDirectoryEntry,
  Framework,
  Site,
  VendorDirectoryEntry,
  VendorVerificationStatus,
  VerificationType,
} from '@mvcp/contracts';
import {
  getDeviceDirectory,
  getDevices,
  getSites,
  getVendors,
  installDevice,
  listApplicableFrameworks,
  listCertificatesForSubject,
  listProposedForSubject,
} from '../api';
import { BandPill } from './bands';
import { CertificateRows } from './CertificateRows';
import { CreateCertificateForm } from './CreateCertificateForm';
import { deviceLabel } from './device-label';
import { ProposeDeviceForm } from './ProposeDeviceForm';
// Story 8.9: the pending-proposal marker has ONE owner; this file used to declare its
// own copy (one of three, across eleven use sites).
import { ProposedTag } from './proposed-tag';
import { RequestCertificateForm } from './RequestCertificateForm';
import type { DrillTarget } from './CockpitShell';
// Story 8.9 reuses Story 7.3's shipped scope control rather than writing a second one —
// a hand-written chip on this page would be exactly the drift `ToggleChip` was extracted
// to prevent.
import { SpineLink, ToggleChip } from './VendorDirectory';

import { DeviceCategoryIcon } from './DeviceCategoryIcon';
import { FormModal } from './FormModal';
/**
 * Rows beyond this count are windowed until the user asks to see all (AD-11).
 *
 * Exported alongside {@link matchesFilter} / {@link byModel} so the vessel screen's Add
 * Device dialog windows at the same count it does — a second constant would let the two
 * device lists in the product disagree about when "Show all" appears.
 */
export const PAGE_SIZE = 50;

/**
 * The lead of the "installed, but the list couldn't refresh" caveat (Story 8.9 review round
 * 2). A constant because two places need to agree on it: the writer in `onInstallProposal`
 * and the sweep in `refreshInUse` that retires it once the counts ARE current — without
 * which a row kept insisting its own correct hint was stale.
 */
const STALE_HINT_PREFIX = 'Installed. The in-use list couldn’t refresh';

/**
 * Verification-status → pill tone. Inlined here (contracts is the source of
 * truth: VENDOR_VERIFICATION_STATUSES + the DESIGN.md status triads) because the
 * web consumes `@mvcp/contracts` as TYPES ONLY — importing a runtime value
 * breaks `vite build` (CJS __exportStar; Story 1.6 debug log). Mirrors the map in
 * `VendorDirectory.tsx`; keep in sync with `packages/contracts/src/entities.ts`.
 */
const TONE: Record<VendorVerificationStatus, 'ok' | 'warn' | 'bad'> = {
  verified: 'ok',
  pending: 'warn',
  unverified: 'bad',
};

/** Human labels for the verification statuses (mirrors the closed contract enum). */
const VERIFICATION_LABEL: Record<VendorVerificationStatus, string> = {
  verified: 'Verified',
  pending: 'Pending',
  unverified: 'Unverified',
};

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; entries: DeviceDirectoryEntry[] }
  | { kind: 'error'; message: string };

/**
 * The page's OUTER selection (Story 8.9), modelled on Story 7.3's shipped
 * `VendorDirectory` scope.
 *
 * `'in-use'` is byte-identically what this page has always been: `GET /devices/directory`,
 * joined on `device_install` — the fleet's in-use set. `'proposals'` is a SEPARATE
 * selection over a SEPARATE source (`GET /devices`, curated rows UNION the caller's own
 * proposals, filtered to the caller's own). The two sets are never unioned: three shipped
 * surfaces read "this page is the in-use fleet" and a silent union would invalidate them
 * (recorded on AD-19).
 */
type Scope = 'in-use' | 'proposals';

/**
 * The ONE filter predicate, shared by both scopes so a category or a search means the same
 * thing in each. Reads only what both row shapes carry — a `DeviceDirectoryEntry` and a
 * `DeviceCatalogEntry` agree on `device` and `vendor`.
 *
 * EXPORTED because the vessel screen's Add Device dialog filters the same catalog rows.
 * Reused rather than mirrored: a second copy is how "Navigation" ends up meaning one thing
 * on the Devices page and another on the vessel that installs them.
 */
export function matchesFilter(
  row: { device: { model: string; category?: string }; vendor: { name: string } },
  category: string,
  query: string,
): boolean {
  const byCategory = category === '' || row.device.category === category;
  const byText =
    query === '' ||
    row.device.model.toLowerCase().includes(query) ||
    row.vendor.name.toLowerCase().includes(query);
  return byCategory && byText;
}

/** Model order, the page's stable sort in both scopes (and in the Add Device dialog). */
export function byModel(
  a: { device: { model: string } },
  b: { device: { model: string } },
): number {
  return a.device.model.localeCompare(b.device.model);
}

function VerificationPill({ status }: { status: VendorVerificationStatus }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={TONE[status]}>
      <span className="status-pill__dot" aria-hidden="true" />
      {VERIFICATION_LABEL[status]}
    </span>
  );
}

/**
 * A neutral compliance-status pill. Status is NEVER color-only (epic UX) — the text
 * is the source of truth. Story 8.1: the word is `BAND_LABEL.neutral` ("Not
 * assessed") via the shared `BandPill`, not the wordier local variant this file used
 * to hardcode — one vocabulary, so the same claim reads the same on every surface.
 */
function NeutralStatusPill(): React.JSX.Element {
  return <BandPill band="neutral" />;
}

/**
 * Compliance Manager cockpit → Devices (Story 2.6, FR-7). The fleet-wide Device
 * Directory: every distinct Device in use across the Company's Sites, fetched
 * once from `GET /devices/directory` (tenant-scoped by the `device_install` RLS)
 * then filtered/searched/sorted/paginated CLIENT-SIDE, mirroring the shipped
 * `VendorDirectory` list→detail shape (well within the epic's low-hundreds
 * scale). A category filter + a model/manufacturer search both narrow the list
 * and are clearable; the default sort is worst-status-first (UX-DR16), which
 * collapses to Device-model order while every status is uniformly neutral until
 * Epic 3. A row opens a FULL-PAGE Device detail (UX-DR11, never a drawer)
 * carrying the manufacturer inline (no cross-section router — Epic 4), a neutral
 * Certificates seam (Epic 3), and the installed-on Sites.
 *
 * Story 7.1 makes the page a WRITE surface too: a "Propose new device" affordance
 * mirroring `VendorDirectory`'s (accent toggle in the toolbar and in both the
 * empty and no-results states, an inline `ProposeDeviceForm`, `readOnly` gating)
 * replaces the old guidance paragraph that told the Manager to go open a Site.
 * Install/remove still live on `SiteDetail` (2.4) — proposing a Device does NOT
 * install it.
 */
export function DeviceDirectory({
  focusId = null,
  onOpenRecord,
  onBack,
  readOnly = false,
}: {
  /**
   * Cockpit drill (Story 4.4): when set, the pane opens the enriched Device detail
   * for this id (cert leaf + owning-Vendor + installed-on-Site drill links) driven
   * by the cockpit back stack. Absent standalone (the Epic-2 list/detail unchanged).
   */
  focusId?: string | null;
  /** Drill to a spine neighbor (the owning Vendor / an installed-on Site) — Story 4.4. */
  onOpenRecord?: (target: DrillTarget) => void;
  /** Pop the cockpit back stack (Story 4.4) — the drilled detail's Back. */
  onBack?: () => void;
  /**
   * Read-only directory reach (Story 7.1), mirroring {@link VendorDirectory}'s
   * prop. When set, suppresses the Compliance-Manager "Propose new device"
   * affordance/form (`POST /devices` is manager-only → 403 for a company-less
   * administrator) and skips the vendor fetch that only the form needs. Default
   * `false` keeps the manager cockpit call site unchanged.
   */
  readOnly?: boolean;
} = {}): React.JSX.Element {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  // Story 8.9: the scope defaults to 'in-use' — this story ADDS a selection to a shipped
  // page, so a Manager who takes no action sees exactly the pre-8.9 Devices page.
  const [scope, setScope] = useState<Scope>('in-use');
  // '' is the "All categories" sentinel. A real category is always trimmed
  // non-empty (server-side), so it can never collide with the sentinel.
  const [category, setCategory] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  // Standalone-mode selection (null = list). In cockpit mode `focusId` drives the
  // detail and this stays null (row clicks route through `onOpenRecord`).
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  // Whether the "Propose new device" form is expanded (Story 7.1).
  const [proposing, setProposing] = useState(false);
  // The Vendor picker's source for that form. The Device Directory itself carries
  // only the manufacturers of devices ALREADY in use, so it cannot offer a vendor
  // whose first device this would be — the form needs the full Directory.
  const [vendors, setVendors] = useState<VendorDirectoryEntry[]>([]);
  const [vendorError, setVendorError] = useState<string | null>(null);
  // Whether that fetch is still in flight. Until it settles the propose buttons are
  // disabled: opening the form early would show an empty picker with no explanation.
  const [vendorsLoading, setVendorsLoading] = useState(!readOnly);
  // What the most recently proposed Device (and, on the inline path, the manufacturer
  // minted with it) was called — the success acknowledgement (the epic bans a silent
  // mutation). Cleared whenever the form is re-opened or cancelled, so a stale "proposed"
  // line can never sit beside a fresh failure and read as success.
  //
  // Story 8.9 widened this from a bare model string: the inline path creates TWO rows, and
  // an acknowledgement that names only one of them leaves the Manager to guess whether the
  // vendor they typed was created at all.
  const [proposedNotice, setProposedNotice] = useState<{
    device: string;
    vendor?: string;
  } | null>(null);
  // --- Story 8.9: the "My proposals" scope ---------------------------------------
  // `GET /devices` — curated rows UNION the caller's own proposals, by RLS. Fetched
  // EAGERLY (see the effect) so the scope's count is honest from first paint.
  const [catalog, setCatalog] = useState<DeviceCatalogEntry[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  // Whether that eager fetch is still in flight. Distinct from `catalog.length === 0`,
  // which is a real answer ("you have proposed nothing") the scope must be able to state —
  // and which the in-flight array is indistinguishable from without this (review round 1).
  const [catalogLoading, setCatalogLoading] = useState(!readOnly);
  // The Sites a proposal can be installed on. Fetched LAZILY, on first entry to the
  // scope: nothing outside it needs Sites, and the in-use page never did.
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesError, setSitesError] = useState<string | null>(null);
  // Whether that lazy fetch SUCCEEDED. Distinct from `sites.length === 0`, which is a
  // real answer ("this Company has no Sites") the row must be able to state — and
  // deliberately NOT set on failure (review round 1): latching it in a `finally` made a
  // dropped request indistinguishable from a Company with no Sites, so the picker stated
  // "No sites yet — create one first" about data it had never seen, permanently, with no
  // way to retry. `sitesError` is the third state, and it is retryable.
  const [sitesLoaded, setSitesLoaded] = useState(false);
  // Per-row install state, keyed by device id: the chosen Site, which rows are in flight,
  // and that row's error. Keyed rather than single-valued because the error must render at
  // the acting control (Story 8.10's lesson, applied here rather than repeated) — and the
  // in-flight SET is keyed for the same reason: a single id let a second row's install
  // re-enable the first one's button mid-flight, so the same POST could be sent twice
  // (review round 1).
  const [sitePick, setSitePick] = useState<Record<string, string>>({});
  const [installingIds, setInstallingIds] = useState<ReadonlySet<string>>(new Set());
  const [installErrors, setInstallErrors] = useState<Record<string, string>>({});
  // What the last successful install put where (review round 2) — the acknowledgement the
  // action shipped without. Cleared when another install starts, so a stale success can never
  // sit beside a fresh failure, which is the invariant `proposedNotice` already carries.
  const [installNotice, setInstallNotice] = useState<{ device: string; site: string } | null>(null);
  const cockpit = onOpenRecord != null;

  /**
   * The in-use list has TWO readers that both write `state` — {@link load} and
   * {@link refreshInUse} — and Story 8.9 calls them from mutation handlers, so they can
   * overlap: an install's refresh and a propose's reload are both in flight after two
   * quick actions. Without a token the slower response wins whichever order it lands in,
   * and a stale `entries` would make the install hint report "Not installed" for a device
   * that was just installed (review round 1). Same idiom as `vendorSeq`/`catalogSeq`.
   */
  const dirSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = (dirSeq.current += 1);
    setState({ kind: 'loading' });
    try {
      const entries = await getDeviceDirectory();
      if (seq !== dirSeq.current) return;
      setState({ kind: 'ready', entries });
    } catch (err: unknown) {
      if (seq !== dirSeq.current) return;
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  /**
   * A NON-DESTRUCTIVE re-read of the in-use list (Story 8.9), for the install action in
   * the proposals scope. Unlike {@link load} it never flips to `loading`: the Manager is
   * looking at the PROPOSALS table, whose install hint is derived from these entries, and
   * dropping to `loading` would make every hint read "install status unavailable" for the
   * duration of a refresh that happened because they succeeded. Returns whether the
   * re-read landed, so the caller can say so rather than silently showing a stale hint.
   */
  const refreshInUse = useCallback(async (): Promise<boolean> => {
    const seq = (dirSeq.current += 1);
    try {
      const entries = await getDeviceDirectory();
      // A superseded refresh still SUCCEEDED — report that to the caller so it does not
      // print a stale-hint warning — it just must not write its older entries over the
      // newer ones.
      if (seq !== dirSeq.current) return true;
      setState({ kind: 'ready', entries });
      // Retire any earlier "couldn't refresh, the count may be out of date" caveat: the
      // counts below are now current, so a row was showing a correct hint beside an alert
      // insisting it was stale (review round 2).
      setInstallErrors((m) => {
        const next: Record<string, string> = {};
        for (const [id, msg] of Object.entries(m)) if (!msg.startsWith(STALE_HINT_PREFIX)) next[id] = msg;
        return next;
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The two manager-only fetches this page makes beyond the in-use list — the vendor
   * directory (for the propose form's picker and its inline datalists) and the device
   * catalog (for the "My proposals" scope).
   *
   * Both are skipped in the same two modes, for the same reason the vendor fetch always
   * was: under `readOnly` there is no propose affordance and no proposals scope at all,
   * and the cockpit drill returns below before the toolbar/form/table ever render, so a
   * request issued there could only waste a round-trip and write an error into state with
   * no render site. (This was `skipVendorFetch` before Story 8.9; the name moved to the
   * condition now that two consumers share it.)
   *
   * A failure of either must not blank the page, which loaded on its own.
   */
  const skipManagerFetch = readOnly || (cockpit && focusId != null);

  /**
   * Both loaders use a SEQUENCE ref rather than the mount effect's `cancelled` flag,
   * because Story 8.9 also calls them from a mutation handler: a token that only the
   * newest call holds discards a stale response whether it was superseded by an unmount
   * or by a later refresh, which the flag cannot do. Same idiom as `ProposeDeviceForm`'s
   * `checkSeq`.
   */
  const vendorSeq = useRef(0);
  const catalogSeq = useRef(0);

  const loadVendors = useCallback(async () => {
    const seq = (vendorSeq.current += 1);
    setVendorsLoading(true);
    try {
      const list = await getVendors();
      if (seq !== vendorSeq.current) return;
      setVendors(list);
      setVendorError(null);
    } catch (err: unknown) {
      if (seq !== vendorSeq.current) return;
      // The last good list STANDS (review round 2). Story 8.9 calls this on a propose SUCCESS
      // path, so blanking it turned a transient re-read failure into the destruction of the
      // picker's contents — a Manager who had just proposed successfully lost the directory.
      // On the very first load `vendors` is already `[]`, so the empty-directory copy below
      // still renders for a Company that genuinely has none.
      setVendorError(err instanceof Error ? err.message : "Couldn't load the vendor directory.");
    } finally {
      if (seq === vendorSeq.current) setVendorsLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    const seq = (catalogSeq.current += 1);
    setCatalogLoading(true);
    try {
      const list = await getDevices();
      if (seq !== catalogSeq.current) return;
      setCatalog(list);
      setCatalogError(null);
    } catch (err: unknown) {
      if (seq !== catalogSeq.current) return;
      // Same rule as `loadVendors` (review round 2): a failed re-read reports itself and
      // leaves the last good catalog in place rather than emptying the scope the Manager is
      // looking at. `scopeCounts` keys the chip off `catalogError`, not off the array, so the
      // count still declines to claim a number.
      setCatalogError(
        err instanceof Error ? err.message : "Couldn't load your proposed devices.",
      );
    } finally {
      if (seq === catalogSeq.current) setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipManagerFetch) {
      setVendorsLoading(false);
      setCatalogLoading(false);
      return;
    }
    void loadVendors();
    // The catalog is fetched EAGERLY, not on first entry to the scope: the scope's count
    // is on screen from first paint, so a lazy fetch would leave it unknown for as long as
    // the Manager stayed out of the scope — and the count is one of the two things that
    // tells them the proposal they just made is reachable at all.
    void loadCatalog();
  }, [skipManagerFetch, loadVendors, loadCatalog]);

  /**
   * The ONE derivation of "the proposals scope is in force" (Story 8.9), mirroring
   * `VendorDirectory`'s `mineScope`. `readOnly` (the admin console) has no single Company,
   * so "my proposals" is meaningless there and the page is pinned to the in-use fleet
   * regardless of state. Everything scope-conditional below reads THIS, never
   * `scope === 'proposals'` directly, so the narrowing, the counts, the empty states and
   * the fetches can never disagree about which view is active.
   */
  const proposalsScope = !readOnly && scope === 'proposals';

  // Sites are needed ONLY by the install control inside the proposals scope, so they are
  // fetched on first entry to it and then kept. `sitesLoaded` is what makes this fire
  // once rather than on every re-entry.
  //
  // A FAILURE does not set `sitesLoaded` (review round 1) — it sets `sitesError`, which
  // gates the effect the same way so it does not spin, and which the retry below clears to
  // re-arm it. Latching a failure as "loaded" is what let the picker announce "No sites yet
  // — create one first" about a Company whose Sites it had never successfully read, with
  // Install inert for the rest of the session and no way back.
  useEffect(() => {
    if (!proposalsScope || sitesLoaded || sitesError) return;
    let cancelled = false;
    void (async () => {
      try {
        const list = await getSites();
        if (cancelled) return;
        setSites(list);
        setSitesError(null);
        setSitesLoaded(true);
      } catch (err: unknown) {
        if (cancelled) return;
        setSites([]);
        setSitesError(err instanceof Error ? err.message : "Couldn't load your sites.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalsScope, sitesLoaded, sitesError]);

  // Clearing the error re-arms the effect above, which is the whole retry.
  function retrySites(): void {
    setSitesError(null);
  }

  /**
   * Story 8.9 RELAXED this. It used to also require `vendorError === null` and a
   * non-empty `vendors`, because a Device had nothing to attach to without a Vendor — so
   * a brand-new Company met a disabled button beneath copy telling it to go propose the
   * manufacturer on another page, which is the "names an action with no route" defect at
   * the moment it bites hardest. The inline path needs no vendor at all, so a failed or
   * empty vendor fetch no longer disables proposing; it only shapes the explanation
   * rendered beside the affordance. The `vendorsLoading` gate stays: opening the form
   * mid-fetch would show an empty picker that is about to fill in.
   */
  const canPropose = !readOnly && !vendorsLoading;

  // Open/close the propose form. Re-opening or cancelling always drops the previous
  // success line: it describes a finished action, not the one being started.
  function toggleProposing(next: boolean): void {
    setProposedNotice(null);
    setProposing(next);
  }

  const entries = state.kind === 'ready' ? state.entries : [];

  /**
   * This Company's own proposals (Story 8.9) — defined by CURATION STATE, never by
   * install state. `GET /devices` returns curated rows UNION the caller's own proposals,
   * so a present `proposedByCompanyId` IS the filter; a row leaves this set when an
   * administrator verifies it (the field is cleared, the UUID is kept) and at no other
   * moment. Installing does not remove a row from here — it makes the SAME row also
   * appear under "In use". Filtering installed rows out would make the list mean
   * "proposals I have not got around to installing", which no surface asks for, and would
   * make a row vanish at the exact moment the Manager acted on it.
   */
  const proposals = useMemo(
    () => catalog.filter((e) => e.device.proposedByCompanyId),
    [catalog],
  );

  /**
   * WHICH of the caller's Sites each device is installed on, derived from the ALREADY LOADED
   * in-use set — never a second fetch, and never a filter on the proposals list.
   *
   * The site IDS are kept, not just the count (review round 2). `POST /sites/:id/devices` is
   * idempotent (`ON CONFLICT DO NOTHING`, and it writes no activity row when nothing was
   * inserted), so offering a Site the device is already on produced a 201, an unchanged
   * count and no observable change of any kind — the Manager could not tell "installed" from
   * "nothing happened". Reducing to a bare number here was what discarded the one thing that
   * could prevent it. `SiteDetail.tsx`'s `assignable` solves the mirror-image case the same way.
   */
  const installedSiteIds = useMemo(() => {
    const byDevice = new Map<string, ReadonlySet<string>>();
    for (const e of entries) byDevice.set(e.device.id, new Set(e.sites.map((s) => s.id)));
    return byDevice;
  }, [entries]);

  /**
   * The install hint for one proposal. THREE states, not two (review round 2): an in-flight
   * read and a failed one are different facts, and reporting either as "not installed" would
   * be a fabricated answer — an empty install set is indistinguishable from a fleet with no
   * installs. `catalog` and `sites` were given this distinction in round 1; this list was not.
   */
  /**
   * The Sites this proposal is NOT already installed on (review round 2) — the only ones an
   * install can change. Offering the rest produced a 201 that did nothing observable, because
   * the endpoint is idempotent, so the Manager could not tell success from a no-op.
   *
   * While the in-use list is unread this offers every Site: the alternative is filtering by an
   * install set that is empty because nothing has loaded, which would silently hide Sites the
   * device is not on. An unnecessary-but-harmless option beats a missing one, and the hint in
   * the same row already says the install status is unknown.
   */
  function installableSites(deviceId: string): Site[] {
    if (state.kind !== 'ready') return sites;
    const on = installedSiteIds.get(deviceId);
    return on ? sites.filter((s) => !on.has(s.id)) : sites;
  }

  function installHint(deviceId: string): string {
    if (state.kind === 'loading') return 'Checking install status…';
    if (state.kind !== 'ready') return 'Install status unavailable';
    const n = installedSiteIds.get(deviceId)?.size ?? 0;
    if (n === 0) return 'Not installed';
    return `Installed on ${n} of your sites`;
  }

  // Distinct categories present in the ACTIVE scope, order-stable (first-seen order): a
  // dropdown offering categories no visible row carries would only lead to the
  // no-matches state. Because the option SET narrows with the scope, switching scope also
  // clears the selection — see `scopeOption`. A retained category whose option had gone
  // filtered every row out while the control rendered as unset (review round 1).
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const e of proposalsScope ? proposals : entries) {
      const c = e.device.category;
      if (c && !seen.includes(c)) seen.push(c);
    }
    return seen;
  }, [entries, proposals, proposalsScope]);

  /**
   * Per-scope counts on the toggle options — ABSOLUTE over the whole loaded set, and
   * deliberately NOT narrowed by the active category or search, so each option advertises
   * the size of the scope it selects (Story 7.3's lesson, restated for this page).
   *
   * `undefined` where the source has not honestly settled (review round 1). Both arrays are
   * empty while their read is in flight AND after it fails, so `.length` would advertise a
   * zero the page invented — "My proposals 0" to a Manager who has proposed something and
   * whose catalog read just dropped, in the default scope where the error does not even
   * render. `ToggleChip` takes `count?: number` for exactly this and shows an em dash.
   */
  /**
   * A catalog error only BLOCKS the scope when there is nothing preserved behind it
   * (review round 3).
   *
   * `loadCatalog`'s catch deliberately leaves the last good catalog in place rather than
   * emptying the scope the Manager is looking at — but every render branch below was gated
   * on `!catalogError`, so the preserved rows were unreachable and the preservation was
   * inert: a failed RETRY made three loaded proposals disappear behind an error paragraph.
   * Gate on this instead, so a re-read failure reports itself ABOVE the data it failed to
   * replace, and only a first-load failure (nothing to show) takes the whole scope.
   */
  const catalogBlocked = catalogError !== null && proposals.length === 0;

  /** Any row carrying the "the in-use list couldn't refresh" caveat — see the control below. */
  const staleInUse = Object.values(installErrors).some((m) => m.startsWith(STALE_HINT_PREFIX));

  const scopeCounts = useMemo(
    () => ({
      'in-use': state.kind === 'ready' ? entries.length : undefined,
      proposals: catalogLoading || catalogBlocked ? undefined : proposals.length,
    }),
    [state.kind, entries, proposals, catalogLoading, catalogBlocked],
  );

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const rows = entries.filter((e) => matchesFilter(e, category, query));
    // Default sort is worst-status-first (UX-DR16), a documented seam. Every
    // status is uniformly neutral until Epic 3, so today the comparator sorts by
    // Device-model only. Epic 3 must ADD a status-rank term ahead of model here
    // (and light up the real bands) — this is a seam that requires that change,
    // not a zero-change no-op. Sort a copy so the fetched array is untouched.
    return [...rows].sort(byModel);
  }, [entries, category, query]);

  // The proposals scope's own pipeline — same predicate, same order, different source.
  const filteredProposals = useMemo(
    () => [...proposals.filter((e) => matchesFilter(e, category, query))].sort(byModel),
    [proposals, category, query],
  );

  const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE);
  const visibleProposals = showAll
    ? filteredProposals
    : filteredProposals.slice(0, PAGE_SIZE);

  /**
   * Accessible names for the per-row Site pickers, and the one case that forces a discriminator
   * (review round 2). `deviceLabel` + the vendor name disambiguates two proposals from
   * different manufacturers — but AD-25 clause 6 states BY NAME that two rows sharing vendor,
   * model and version are a legitimate, deliberately unconstrained catalog state, creatable
   * through this very form (the duplicate disclosure is advisory, never a refusal). So the
   * label is the readable one whenever it is unique among the visible proposals, and gains a
   * short id only for the rows that actually collide — rather than making every AT user read
   * a UUID fragment to serve a case most Companies never hit.
   */
  const pickerLabels = useMemo(() => {
    const base = new Map<string, string>();
    const seen = new Map<string, number>();
    for (const e of visibleProposals) {
      const label = `${deviceLabel(e.device)} by ${e.vendor.name}`;
      base.set(e.device.id, label);
      seen.set(label, (seen.get(label) ?? 0) + 1);
    }
    const out = new Map<string, string>();
    for (const [id, label] of base) {
      out.set(id, (seen.get(label) ?? 0) > 1 ? `${label} (${id.slice(0, 8)})` : label);
    }
    return out;
  }, [visibleProposals]);

  const hasActiveFilter = category !== '' || query !== '';
  function clearFilters(): void {
    setCategory('');
    setSearch('');
    setShowAll(false);
  }

  /**
   * A Device was proposed: clear any active category filter/search, collapse the form,
   * then re-read everything the new row can show up in.
   *
   * What clearing the filter guarantees is narrow but real: afterwards the Manager sees
   * the WHOLE of whichever scope they are in, so no row is hidden behind a stale filter
   * left over from before the mutation. It still does NOT put the new Device in the
   * **in-use** list — that is `GET /devices/directory`, which joins `device_install`, so a
   * proposed-but-uninstalled Device is structurally absent from it. What Story 8.9 changes
   * is that the row is no longer unreachable: it is in the catalog re-read below, and
   * therefore under "My proposals", where it can be installed.
   *
   * The VENDOR re-read is conditional and load-bearing. Both this page and `SiteDetail`
   * fetch vendors exactly once, so after an inline propose the form's picker would still
   * be missing the vendor just minted — and the Manager's cheapest next move would be to
   * mint a second copy of it. Since the whole mitigation for inline duplicates is "read
   * the directory first", a stale picker turns that mitigation into its opposite. On the
   * `vendorId` path nothing about the vendor list changed, so it is not re-fetched.
   *
   * The form COLLAPSES LAST, after the re-reads land (review round 1) — the spec's wording
   * is "re-fetches vendors on whichever parent is mounted, BEFORE the form collapses", and
   * collapsing first left a window in which re-opening showed a picker that was still
   * missing the vendor just minted. Here that window was closed only incidentally, by
   * `vendorsLoading` disabling the propose buttons; ordering it correctly is what makes the
   * guarantee independent of that coincidence, and `SiteDetail` had no such coincidence.
   */
  const onProposed = useCallback(
    async (mintedVendor: boolean) => {
      setCategory('');
      setSearch('');
      setShowAll(false);
      await Promise.all([
        // `refreshInUse`, not `load()` (review round 2). `load()` flips to `kind: 'loading'`,
        // which is exactly what `refreshInUse` was written to avoid: a propose made from
        // inside the proposals scope would drop every row's install hint to "Checking…" and
        // both chips to an em dash for the duration of a re-read that happened because the
        // Manager succeeded. The reasoning was already in `refreshInUse`'s docblock and had
        // simply not been applied to its second caller.
        refreshInUse(),
        loadCatalog(),
        ...(mintedVendor ? [loadVendors()] : []),
      ]);
      setProposing(false);
    },
    [refreshInUse, loadCatalog, loadVendors],
  );

  /**
   * Install one proposal on a chosen Site, from the row that lists it (Story 8.9).
   *
   * This is a ROUTE to the existing `POST /sites/:siteId/devices`, not a second install
   * path: the endpoint, its semantics and `SiteDetail`'s canonical picker are untouched.
   * The install is created against the proposal's OWN canonical UUID, so when an
   * administrator later verifies the row (clearing `proposed_by_company_id`) the install
   * survives with zero re-points (AD-19 clause 3/4).
   *
   * On success the in-use list is re-read so the "installed on N of your sites" hint is
   * true immediately; the row itself STAYS under "My proposals", because it is still a
   * proposal. A refresh that fails is reported at the control rather than swallowed — the
   * install did happen, and a hint that silently disagrees with it is worse than a
   * sentence saying so.
   */
  async function onInstallProposal(entry: DeviceCatalogEntry): Promise<void> {
    const deviceId = entry.device.id;
    const picked = sitePick[deviceId] ?? '';
    // A retained pick whose `<option>` has since been FILTERED OUT — the device was
    // installed on that Site from `SiteDetail` or another tab, and a later `refreshInUse`
    // dropped it from `installableSites` — renders as the placeholder but was still
    // POSTed, and the endpoint's `ON CONFLICT DO NOTHING` turned it into a 201 that
    // changed nothing, announced as a fresh install (review round 3). A pick with no
    // option is not a pick.
    const siteId = installableSites(deviceId).some((s) => s.id === picked) ? picked : '';
    setInstallErrors((m) => ({ ...m, [deviceId]: '' }));
    // BEFORE the early return (review round 3). This clear is what makes the docblock's
    // "a stale success can never sit beside a fresh failure" true, and below the return it
    // was skipped on the one failure the row can produce without a request — which a
    // successful install then makes the NEXT likely click, because success resets
    // `sitePick` to '' and the button stays enabled.
    setInstallNotice(null);
    if (!siteId) {
      setInstallErrors((m) => ({ ...m, [deviceId]: 'Select a site to install on.' }));
      return;
    }
    setInstallingIds((s) => new Set(s).add(deviceId));
    try {
      await installDevice(siteId, deviceId);
      setSitePick((m) => ({ ...m, [deviceId]: '' }));
      const refreshed = await refreshInUse();
      if (refreshed) {
        // SAY SO (review round 2). Nothing acknowledged a successful install: the only
        // feedback channel was `installErrors`, populated solely on failure, and the hint
        // text sits in a plain `<span className="muted">` that no AT announces. The epic bans
        // a silent mutation, which is why this same page mounts a `role="status"` region for
        // propose — install is the mutation Story 8.9 adds, and it had none.
        setInstallNotice({
          device: deviceLabel(entry.device),
          site: sites.find((s) => s.id === siteId)?.name ?? 'the selected site',
        });
      } else {
        setInstallErrors((m) => ({
          ...m,
          [deviceId]: `${STALE_HINT_PREFIX}, so the install count below may be out of date.`,
        }));
      }
    } catch (err: unknown) {
      setInstallErrors((m) => ({
        ...m,
        [deviceId]: err instanceof Error ? err.message : "Couldn't install. Try again.",
      }));
    } finally {
      // Remove only THIS row (review round 1). A single `installingId` meant a second row
      // starting an install re-enabled the first row's button while its POST was still in
      // flight, so the same install could be sent twice.
      setInstallingIds((s) => {
        const next = new Set(s);
        next.delete(deviceId);
        return next;
      });
    }
  }

  // Open a Device's detail: drill through the cockpit back stack when wired, else the
  // standalone local selection (Epic-2 behavior).
  //
  // Navigating away also retires the propose acknowledgement. The detail branch
  // returns before this section renders but the component stays MOUNTED, so without
  // this a Back from a drill would land the Manager on a "was proposed" line
  // describing an action two navigations ago.
  function openDevice(id: string): void {
    setProposedNotice(null);
    if (onOpenRecord) onOpenRecord({ kind: 'device', id });
    else setSelectedDeviceId(id);
  }

  const selected = selectedDeviceId
    ? entries.find((e) => e.device.id === selectedDeviceId) ?? null
    : null;

  // The scope group, rendered through Story 7.3's shipped chip (Story 8.9).
  const scopeOption = (key: Scope, label: string): React.JSX.Element => (
    <ToggleChip
      label={label}
      count={scopeCounts[key]}
      active={scope === key}
      onClick={() => {
        setScope(key);
        setShowAll(false);
        // The install surfaces are scope-local, so they must not survive the switch
        // (review round 3). `installNotice`'s `role="status"` region is mounted
        // unconditionally — precisely so an announcement is not missed — which means
        // leaving it populated re-mounts it already full on the way back and re-announces
        // an install completed several interactions ago. `installErrors` (including the
        // stale-count caveat) carry the same staleness. `toggleProposing` already clears
        // `proposedNotice` for this exact reason.
        setInstallNotice(null);
        setInstallErrors({});
        // The category OPTION SET is scope-dependent, so a selection carried across the
        // switch could be in force with no option to represent it — the control rendered
        // as unset while every row was filtered out, and the only visible explanation was
        // a no-matches line naming a filter the Manager could not see (review round 1).
        // `search` is deliberately kept: its value is visible in the input, so it explains
        // its own effect.
        setCategory('');
      }}
    />
  );

  // ---- Cockpit drill: the enriched Device detail for `focusId` (Story 4.4) ----
  if (cockpit && focusId) {
    const back = onBack ?? (() => {});
    const entry = entries.find((e) => e.device.id === focusId) ?? null;
    if (state.kind === 'loading') {
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        </>
      );
    }
    if (state.kind === 'error') {
      // A directory LOAD failure is NOT a missing record — surface the error (never a
      // misleading "no longer in use") with a Retry; Back still works.
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <section className="card stack" role="alert">
            <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Couldn’t load this device</h1>
            <p className="muted" style={{ margin: 0 }}>{state.message}</p>
            <button type="button" className="btn-accent" data-surface="accent" onClick={() => void load()} style={{ alignSelf: 'flex-start' }}>
              Retry
            </button>
          </section>
        </>
      );
    }
    if (!entry) {
      // A stale/unknown drill target: graceful not-found within the pane; Back works.
      return (
        <>
          <button type="button" className="icon-button" onClick={back} style={{ alignSelf: 'flex-start' }}>
            ← Back
          </button>
          <section className="card stack">
            <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Device not found</h1>
            <p className="muted" style={{ margin: 0 }}>This Device is no longer in use across your fleet.</p>
          </section>
        </>
      );
    }
    return <DeviceDetail key={focusId} entry={entry} onBack={back} onOpenRecord={onOpenRecord} />;
  }

  // ---- Full-page Device detail (UX-DR11: NOT a drawer) ----
  if (selected) {
    const d = selected.device;
    const v = selected.vendor;
    return (
      <>
        <button
          type="button"
          className="icon-button"
          onClick={() => setSelectedDeviceId(null)}
          style={{ alignSelf: 'flex-start' }}
        >
          ← Back to devices
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
            {d.model}
          </h1>
          <NeutralStatusPill />
          {d.proposedByCompanyId ? <ProposedTag /> : null}
        </div>

        <section className="card stack" aria-labelledby="device-overview-heading">
          <h2 id="device-overview-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Overview
          </h2>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
            <dt className="muted">Version</dt>
            <dd style={{ margin: 0 }}>
              {d.version ? <span data-numeric>{d.version}</span> : '—'}
            </dd>
            <dt className="muted">Category</dt>
            <dd style={{ margin: 0 }}>{d.category ?? '—'}</dd>
            <dt className="muted">Manufacturer</dt>
            <dd style={{ margin: 0 }}>
              <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {v.name}
                <VerificationPill status={v.verificationStatus} />
                {v.proposedByCompanyId ? <ProposedTag /> : null}
              </span>
            </dd>
            <dt className="muted">Company status</dt>
            <dd style={{ margin: 0 }}>
              <NeutralStatusPill />
            </dd>
          </dl>
          <p className="muted" style={{ margin: 0 }}>
            The manufacturer's full profile lives in the Vendors section (cross-section
            navigation arrives in Epic 4).
          </p>
        </section>

        <section className="card stack" aria-labelledby="device-certs-heading">
          <h2 id="device-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Certificates
          </h2>
          <NeutralStatusPill />
          <p className="muted" style={{ margin: 0 }}>Certificate scoring arrives in Epic 3.</p>
        </section>

        <section className="card stack" aria-labelledby="device-sites-heading">
          <h2 id="device-sites-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Installed on
          </h2>
          {selected.sites.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Not installed on any Site.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {selected.sites.map((s) => (
                <li key={s.id}>
                  {s.name} <span className="muted">({s.type})</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </>
    );
  }

  // ---- Directory list ----
  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Devices</h1>

      <section className="card stack" aria-labelledby="devices-heading">
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h2 id="devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Device Directory
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Story 8.9 scope, through Story 7.3's shipped `ToggleChip`. Suppressed
                under readOnly: an administrator has no Company, so "My proposals" would
                be a permanently empty tab. */}
            {!readOnly && (
              <div role="group" aria-label="Device scope" style={{ display: 'flex', gap: 8 }}>
                {scopeOption('in-use', 'In use')}
                {scopeOption('proposals', 'My proposals')}
              </div>
            )}
            <select
              aria-label="Filter by category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setShowAll(false);
              }}
              style={{
                padding: '6px 10px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rounded-md)',
                color: 'var(--text)',
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
              }}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="search"
              aria-label="Search devices by model or manufacturer"
              placeholder="Search by model or manufacturer…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowAll(false);
              }}
              style={{
                padding: '6px 10px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rounded-md)',
                color: 'var(--text)',
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
              }}
            />
            {hasActiveFilter && (
              <button type="button" className="icon-button" onClick={clearFilters}>
                Clear
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => toggleProposing(!proposing)}
                aria-expanded={proposing}
                disabled={!canPropose}
              >
                Propose new device
              </button>
            )}
          </div>
        </div>

        {/* Installing is a Site-level action (Story 2.4) and nothing else on this
            page says so — the one fact worth keeping from the guidance block the
            propose affordance replaced.

            Story 8.9 finished the sentence. It used to end "install it on a Site to put
            it in service", which named an action with no route from this page: the row it
            describes is structurally absent from the in-use list. Now there is a route,
            and this says where. */}
        {!readOnly && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Proposing adds a catalog row. It appears under <strong>My proposals</strong>,
            where you can install it on one of your sites to put it in service.
          </p>
        )}

        {/* The vendor picker feeds the propose form only. Story 8.9: a load failure no
            longer makes proposing unavailable — the inline path needs no vendor at all —
            so this explains what the Manager has lost (the picker) and what still works,
            rather than announcing a dead end that is no longer real. */}
        {/* The wording no longer asserts the picker is EMPTY (review round 2): a failed
            re-read now leaves the last good list in place, so "will be empty" was only true
            on a first-load failure and false on every refresh failure — and this same page
            calls that refresh on a propose SUCCESS path. "Incomplete or out of date" is true
            in both cases, which is what the sentence has to be. */}
        {!readOnly && vendorError && (
          <p role="alert" className="muted" style={{ margin: 0, fontSize: 13 }}>
            Couldn&rsquo;t load the manufacturer list, so the picker in the propose form may
            be incomplete or out of date — you can still propose a device together with its
            manufacturer: {vendorError}
          </p>
        )}

        {/* Loaded fine, but the directory is empty — a brand-new Company. Story 8.9: this
            used to say the device had "nothing to attach to" and point at the Vendors
            page, beside a DISABLED button. Both halves were the defect: the propose
            affordance is now enabled and the route is inside the form. */}
        {!readOnly && !vendorsLoading && !vendorError && vendors.length === 0 && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            No manufacturers in your directory yet — propose the device and its
            manufacturer together using <strong>Propose new device</strong> above.
          </p>
        )}

        {!readOnly && proposing && (
          <FormModal title="Propose a new device" titleId="propose-device-heading" onClose={() => toggleProposing(false)} wide>
          <ProposeDeviceForm
            vendors={vendors}
            // RETURNS the promise (review round 2). Dropping it with `void` let the form
            // re-enable its submit button while these re-reads were still in flight, with
            // the form still mounted and its fields intact — a second click then minted a
            // second Device and a second Vendor.
            onCreated={(entry, mintedVendor) => {
              setProposedNotice({
                device: deviceLabel(entry.device),
                // Name the manufacturer only when this propose MINTED it: on the picker
                // path the Manager chose it themselves.
                ...(mintedVendor ? { vendor: entry.vendor.name } : {}),
              });
              return onProposed(mintedVendor);
            }}
            onCancel={() => toggleProposing(false)}
          />
          </FormModal>
        )}

        {/* A proposed Device is a catalog row, not an installation — the "In use" scope
            is the fleet's in-use set, so the new row only joins it once the Manager
            installs it on a Site. Say so explicitly: the epic bans silent success. Story
            8.9 also names the manufacturer when the inline path minted one, and points at
            the scope that now holds the row instead of at an action with no route.

            The live region is mounted UNCONDITIONALLY (empty until there is
            something to say). A `role="status"` element that first appears at the
            moment of the update is announced unreliably by screen readers, which
            would make this an effectively silent success for an AT user. While it is
            empty it is taken OUT of flow (`position: absolute`) rather than merely
            given no content: `.stack` lays this section out with `gap`, so an empty
            in-flow flex item would open a permanent strip of dead space above the
            table. */}
        {!readOnly && (
          <p
            role="status"
            className="muted"
            style={
              proposedNotice
                ? { margin: 0, fontSize: 13 }
                : { position: 'absolute', width: 1, height: 1, overflow: 'hidden' }
            }
          >
            {proposedNotice &&
              (proposedNotice.vendor ? (
                <>
                  <strong>{proposedNotice.device}</strong> and its manufacturer{' '}
                  <strong>{proposedNotice.vendor}</strong> were proposed and are awaiting
                  admin verification. Find the device under <strong>My proposals</strong>{' '}
                  to install it on a site.
                </>
              ) : (
                <>
                  <strong>{proposedNotice.device}</strong> was proposed and is awaiting
                  admin verification. Find it under <strong>My proposals</strong> to
                  install it on a site.
                </>
              ))}
          </p>
        )}

        {/* Every branch below belongs to the IN-USE scope, hence the `!proposalsScope`
            gate on each: the in-use fetch's loading and error states are facts about a
            list the proposals scope does not show, and a Manager reading their own
            proposals must not be told the fleet failed to load. The proposals scope's own
            states follow. */}
        {!proposalsScope && state.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        )}

        {!proposalsScope && state.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0 }}>
            {state.message}
          </p>
        )}

        {/* Empty state (no data at all) — distinct from the no-matches state
            below, and the one a brand-new Company sees. It gets the SAME primary
            action: an empty fleet is exactly when "there is no way to add a device
            from here" bites hardest, and copy that only points off to a Site is
            the guidance block this story set out to replace. */}
        {!proposalsScope && state.kind === 'ready' && entries.length === 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>
              {readOnly
                ? 'No devices in use yet.'
                : 'No devices in use yet. Propose a device, then install it on one of your Sites to see it here.'}
            </p>
            {!readOnly && !proposing && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => toggleProposing(true)}
                style={{ alignSelf: 'flex-start' }}
                disabled={!canPropose}
              >
                Propose new device
              </button>
            )}
          </div>
        )}

        {!proposalsScope && state.kind === 'ready' && entries.length > 0 && filtered.length === 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>No devices match your filter or search.</p>
            {!readOnly && !proposing && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => toggleProposing(true)}
                style={{ alignSelf: 'flex-start' }}
                disabled={!canPropose}
              >
                Propose new device
              </button>
            )}
          </div>
        )}

        {!proposalsScope && state.kind === 'ready' && filtered.length > 0 && (
          <>
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Version</th>
                  <th>Category</th>
                  <th>Manufacturer</th>
                  <th>Company status</th>
                  <th>Device status</th>
                  <th>Installed on</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr
                    key={e.device.id}
                    onClick={() => openDevice(e.device.id)}
                    data-clickable="true"
                  >
                    <td>
                      <span className="cell-identity">
                        {/* The equipment-category glyph, not a monogram of the model: the
                            model is spelled out in full immediately to its right, so two
                            initials of it spent the row's one pre-attentive slot restating
                            text. Renders nothing when no category is recorded. */}
                        <DeviceCategoryIcon category={e.device.category} />
                        <button
                          type="button"
                          className="cell-link"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            openDevice(e.device.id);
                          }}
                        >
                          {e.device.model}
                        </button>
                        {e.device.proposedByCompanyId ? <ProposedTag /> : null}
                      </span>
                    </td>
                    {/* Versions are identifiers → the monospace face (UX type rule);
                        a version-less legacy row renders the em-dash placeholder. */}
                    <td>
                      {e.device.version ? <span data-numeric>{e.device.version}</span> : '—'}
                    </td>
                    <td>{e.device.category ?? '—'}</td>
                    <td>{e.vendor.name}</td>
                    <td>
                      <NeutralStatusPill />
                    </td>
                    <td>
                      <NeutralStatusPill />
                    </td>
                    <td>
                      <span data-numeric>{e.sites.length}</span>{' '}
                      {e.sites.length === 1 ? 'site' : 'sites'}
                      <div className="muted" style={{ fontSize: 13 }}>
                        {e.sites.map((s) => s.name).join(', ')}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filtered.length > PAGE_SIZE && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  Showing <span data-numeric>{visible.length}</span> of{' '}
                  <span data-numeric>{filtered.length}</span>
                </span>
                {!showAll && (
                  <button type="button" className="icon-button" onClick={() => setShowAll(true)}>
                    Show all
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* ---- Story 8.9: the "My proposals" scope ------------------------------ */}

        {/* A failed catalog read must not blank the page — the toolbar, the propose
            affordance and the in-use scope all still work — so it degrades to an
            explanation in place of the table. */}
        {/* RETRYABLE (review round 2). `loadCatalog` runs only from the mount effect and
            from `onProposed`, so one dropped read left "My proposals" an em dash for the
            session behind an explanation that named no action — the identical dead end round
            1 fixed for `GET /sites` one component over, left standing for the read this scope
            exists to show. */}
        {proposalsScope && catalogError && (
          <p role="alert" className="muted" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Couldn&rsquo;t load your proposed devices: {catalogError}</span>
            <button type="button" className="icon-button" onClick={() => void loadCatalog()}>
              Retry
            </button>
          </p>
        )}

        {/* The install acknowledgement (review round 2). Mounted UNCONDITIONALLY for the same
            reason the propose notice is: a `role="status"` element that first APPEARS at the
            moment of the update is announced unreliably, which would make this a silent
            success for an AT user — and install had no acknowledgement at all. */}
        {proposalsScope && (
          <p
            role="status"
            className="muted"
            style={
              installNotice
                ? { margin: 0, fontSize: 13 }
                : { position: 'absolute', width: 1, height: 1, overflow: 'hidden' }
            }
          >
            {installNotice && (
              <>
                <strong>{installNotice.device}</strong> is now installed on{' '}
                <strong>{installNotice.site}</strong>. It stays under &ldquo;My
                proposals&rdquo; until an administrator verifies it.
              </>
            )}
          </p>
        )}

        {/* Still reading. Distinct from the empty state below (review round 1): an
            in-flight catalog and a Company that has proposed nothing both present as an
            empty array, and telling a Manager who has just proposed something that they
            have proposed nothing is the dead end this scope exists to remove. */}
        {proposalsScope && catalogLoading && !catalogBlocked && (
          <p className="muted" style={{ margin: 0 }}>
            Loading your proposed devices&hellip;
          </p>
        )}

        {/* The scope's OWN empty state: no proposals at all. Distinct from the in-use
            empty state (a different question) and from the no-matches state below
            (clearing a filter would not help), and it offers the one action that does. */}
        {proposalsScope && !catalogLoading && !catalogBlocked && proposals.length === 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ margin: 0 }}>
              You haven&rsquo;t proposed any devices. A device you propose waits here until
              an administrator verifies it — and you can install it on your sites in the
              meantime.
            </p>
            {!proposing && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => toggleProposing(true)}
                style={{ alignSelf: 'flex-start' }}
                disabled={!canPropose}
              >
                Propose new device
              </button>
            )}
          </div>
        )}

        {proposalsScope &&
          !catalogLoading &&
          !catalogBlocked &&
          proposals.length > 0 &&
          filteredProposals.length === 0 && (
            <p className="muted" style={{ margin: 0 }}>
              None of your proposals match your filter or search.
            </p>
          )}

        {/* The Sites read is PAGE-scoped, so it is reported once, here — not inside each
            row's assertive alert, which announced it N times and occupied the slot that
            row's own install error needs (review round 1). It is retryable: the install
            control is inert until Sites are known, and without this there was no way back. */}
        {proposalsScope && sitesError && (
          <p role="alert" className="muted" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              Couldn&rsquo;t load your sites, so installing is unavailable: {sitesError}
            </span>
            <button type="button" className="icon-button" onClick={retrySites}>
              Retry
            </button>
          </p>
        )}

        {/* The IN-USE read, reported inside the proposals scope (review round 3). It is the
            third read this scope depends on — the catalog above and Sites beside it both
            got an explanation and a Retry, and this one had neither, because every in-use
            branch is gated on `!proposalsScope`. Without it a failed `GET /devices/directory`
            made every row read "Install status unavailable" with no cause and no way back,
            while `installableSites` fell open to offering every Site. */}
        {proposalsScope && state.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              Couldn&rsquo;t load your in-use fleet, so the install status below is unknown:{' '}
              {state.message}
            </span>
            <button type="button" className="icon-button" onClick={() => void load()}>
              Retry
            </button>
          </p>
        )}

        {/* A refresh that failed AFTER an install leaves the counts stale without moving
            `state` off `ready`, so the branch above cannot catch it. The row carries the
            per-row caveat; this is the missing ACTION, deliberately not a second copy of
            the sentence and deliberately not a live region — the row's alert already spoke. */}
        {proposalsScope && state.kind === 'ready' && staleInUse && (
          <p className="muted" style={{ margin: 0 }}>
            <button type="button" className="icon-button" onClick={() => void refreshInUse()}>
              Refresh install status
            </button>
          </p>
        )}

        {proposalsScope && !catalogLoading && !catalogBlocked && filteredProposals.length > 0 && (
          <>
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Version</th>
                  <th>Category</th>
                  <th>Manufacturer</th>
                  <th>Installed</th>
                  <th>Install on a site</th>
                </tr>
              </thead>
              <tbody>
                {visibleProposals.map((e) => (
                  <tr key={e.device.id}>
                    <td>
                      <span className="cell-identity">
                        <DeviceCategoryIcon category={e.device.category} />
                        <span style={{ fontWeight: 600 }}>{e.device.model}</span>
                        {/* Every row here is a proposal by construction, but the marker is
                            still rendered per row rather than once in the heading: this
                            table is one of eleven places the same fact appears, and a
                            scope-level caption would be a twelfth wording of it. */}
                        <ProposedTag />
                      </span>
                    </td>
                    {/* Versions are identifiers → the monospace face (UX type rule);
                        a version-less row renders the em-dash placeholder. */}
                    <td>
                      {e.device.version ? <span data-numeric>{e.device.version}</span> : '—'}
                    </td>
                    <td>{e.device.category ?? '—'}</td>
                    <td>
                      <span className="cell-cluster">
                        {e.vendor.name}
                        {/* An inline-path device is born with an unverified owning vendor,
                            and that is exactly the state in which an administrator must
                            verify the VENDOR first — so naming it here is not decoration. */}
                        {e.vendor.proposedByCompanyId ? <ProposedTag /> : null}
                      </span>
                    </td>
                    <td>
                      <span className="muted">{installHint(e.device.id)}</span>
                    </td>
                    <td>
                      <div className="stack" style={{ gap: 6 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select
                            // The VENDOR is part of the name (review round 1), plus a short
                            // id for rows that still collide (review round 2) — see
                            // `pickerLabels`. Without it two pickers share one accessible
                            // name, which is ambiguous to a screen reader and unaddressable
                            // to a test.
                            aria-label={`Site to install ${pickerLabels.get(e.device.id) ?? deviceLabel(e.device)} on`}
                            value={sitePick[e.device.id] ?? ''}
                            disabled={!sitesLoaded || installableSites(e.device.id).length === 0}
                            onChange={(ev) => {
                              setSitePick((m) => ({ ...m, [e.device.id]: ev.target.value }));
                              setInstallErrors((m) => ({ ...m, [e.device.id]: '' }));
                            }}
                            style={{
                              padding: '6px 10px',
                              background: 'var(--surface-2)',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--rounded-md)',
                              color: 'var(--text)',
                              fontFamily: 'var(--font-sans)',
                              fontSize: 14,
                            }}
                          >
                            {/* A Company with no Sites is stated IN the picker and the
                                button goes inert, rather than an empty picker that fails
                                only once the Manager presses Install.
                                Three states, not two (review round 1): "no sites" is a
                                claim about the Company's data and may only be made once
                                the read has SUCCEEDED. A failed or pending read says so
                                instead — the cause and the retry are in the page-level
                                alert above. */}
                            <option value="">
                              {sitesError
                                ? 'Sites unavailable'
                                : !sitesLoaded
                                  ? 'Loading your sites…'
                                  : sites.length === 0
                                    ? 'No sites yet — create one first'
                                    : installableSites(e.device.id).length === 0
                                      ? 'Already on all your sites'
                                      : 'Select a site…'}
                            </option>
                            {installableSites(e.device.id).map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn-accent"
                            data-surface="accent"
                            onClick={() => void onInstallProposal(e)}
                            disabled={
                              installingIds.has(e.device.id) ||
                              !sitesLoaded ||
                              installableSites(e.device.id).length === 0
                            }
                          >
                            {installingIds.has(e.device.id) ? 'Installing…' : 'Install'}
                          </button>
                        </div>
                        {/* THIS ROW's install error, and nothing else (review round 1).
                            The error belongs AT the acting control, not in a page-level
                            banner rows away from it (the defect Story 8.10 owns on the
                            admin side — this surface is born without it) — and the
                            converse holds too: a page-level fact like a failed Sites read
                            must not be announced once per row from an assertive region. */}
                        {/* Mounted only for the row that HAS something to say (review round
                            2). `role="alert"` already implies assertive + atomic, and up to
                            fifty always-present assertive regions is a great deal of
                            interrupt surface for a table where at most one row is acting. */}
                        {installErrors[e.device.id] && (
                          <div role="alert">
                            <span className="status-pill" data-tone="bad">
                              <span className="status-pill__dot" aria-hidden="true" />
                              {installErrors[e.device.id]}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filteredProposals.length > PAGE_SIZE && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  Showing <span data-numeric>{visibleProposals.length}</span> of{' '}
                  <span data-numeric>{filteredProposals.length}</span>
                </span>
                {!showAll && (
                  <button type="button" className="icon-button" onClick={() => setShowAll(true)}>
                    Show all
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

type DetailState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      certs: Certificate[];
      /** The manager's OWN pending submissions for this Device (Story 6.10). */
      declared: DeclaredCertificate[];
      frameworks: Framework[];
      frameworkCode: (frameworkId: string) => string;
      frameworkVerificationType: (frameworkId: string) => VerificationType | undefined;
    }
  | { kind: 'error'; message: string };

/**
 * The enriched cockpit Device detail (Story 4.4). Consumes only existing read
 * endpoints (no new API/compute): its own certificates (`listCertificatesForSubject`)
 * as the read-only leaf, plus the owning Vendor and installed-on Sites — both carried
 * on the directory `entry` already — rendered as drill links. Header/overview data
 * comes from the loaded `entry` so it renders immediately; certificates load
 * asynchronously with their own loading/error/empty states.
 */
function DeviceDetail({
  entry,
  onBack,
  onOpenRecord,
}: {
  entry: DeviceDirectoryEntry;
  onBack: () => void;
  onOpenRecord?: (target: DrillTarget) => void;
}): React.JSX.Element {
  const d = entry.device;
  const v = entry.vendor;
  const [detail, setDetail] = useState<DetailState>({ kind: 'loading' });
  // Whether the "Add certificate" form is expanded (Story 6.8).
  const [adding, setAdding] = useState(false);
  // Whether the "Request certificate from vendor" form is expanded (Story 6.9).
  const [requesting, setRequesting] = useState(false);
  // Bumped to force a certificate re-read after a create/attach (Story 6.8).
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setDetail({ kind: 'loading' });
      try {
        const [certs, declared, frameworks] = await Promise.all([
          listCertificatesForSubject('device', d.id),
          // The manager's OWN pending submissions for this Device (Story 6.10).
          // AUXILIARY: degrade to an empty list on failure rather than joining the
          // Promise.all rejection — a 500 here must not blank out the certificate list,
          // which loaded independently long before 6.10 existed.
          listProposedForSubject('device', d.id).catch(() => []),
          listApplicableFrameworks(),
        ]);
        if (cancelled) return;
        const codeMap = new Map(frameworks.map((f) => [f.id, f.code]));
        // Framework id → verification_type, the orthogonal provenance marker. Company
        // screens carry it for the same reason the vendor portal does: a self-declared
        // certificate can read "Certified", and without the marker that reads as a
        // third-party one (DESIGN.md provenance-marker / PRD §5.5 honesty rule).
        const vtMap = new Map(frameworks.map((f) => [f.id, f.verificationType]));
        setDetail({
          kind: 'ready',
          certs,
          declared,
          frameworks,
          frameworkCode: (id) => codeMap.get(id) ?? id,
          frameworkVerificationType: (id) => vtMap.get(id),
        });
      } catch (err: unknown) {
        if (!cancelled) {
          setDetail({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [d.id, reloadToken]);

  // A certificate was created (or its evidence changed): collapse the form and
  // re-read this Device's certificate list so the new/updated row is reflected.
  const onCertMutated = useCallback(() => {
    setAdding(false);
    setReloadToken((t) => t + 1);
  }, []);

  return (
    <>
      <button type="button" className="icon-button" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ← Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>{d.model}</h1>
        {d.proposedByCompanyId ? <ProposedTag /> : null}
      </div>

      <section className="card stack" aria-labelledby="device-overview-heading">
        <h2 id="device-overview-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Overview</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
          <dt className="muted">Version</dt>
          <dd style={{ margin: 0 }}>{d.version ? <span data-numeric>{d.version}</span> : '—'}</dd>
          <dt className="muted">Category</dt>
          <dd style={{ margin: 0 }}>{d.category ?? '—'}</dd>
          <dt className="muted">Manufacturer</dt>
          <dd style={{ margin: 0 }}>
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* The owning Vendor — drill up the spine to its profile (Story 4.4). */}
              <SpineLink label={v.name} onClick={() => onOpenRecord?.({ kind: 'vendor', id: v.id })} />
              <VerificationPill status={v.verificationStatus} />
              {v.proposedByCompanyId ? <ProposedTag /> : null}
            </span>
          </dd>
        </dl>
      </section>

      <section className="card stack" aria-labelledby="device-certs-heading">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <h2 id="device-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Certificates</h2>
          {detail.kind === 'ready' && !adding && !requesting && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => setAdding(true)}
                aria-expanded={adding}
              >
                Add certificate
              </button>
              <button
                type="button"
                className="icon-button"
                onClick={() => setRequesting(true)}
                aria-expanded={requesting}
              >
                Request certificate from vendor
              </button>
            </div>
          )}
        </div>
        {detail.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
        {detail.kind === 'error' && (
          <p role="alert" className="muted" style={{ margin: 0 }}>{detail.message}</p>
        )}
        {detail.kind === 'ready' && adding && (
          <FormModal title="Add certificate" titleId="add-cert-heading" onClose={() => setAdding(false)} wide>
          <CreateCertificateForm
            frameworks={detail.frameworks.filter((f) => f.scope === 'device')}
            vendorId={v.id}
            // Story 8.5 (AD-25): the ref carries model + revision, not a baked label —
            // the form composes it through the one web owner.
            devices={[
              {
                id: d.id,
                model: d.model,
                ...(d.version ? { version: d.version } : {}),
                // Story 8.9: the subject may still be this Company's own proposal.
                ...(d.proposedByCompanyId ? { proposed: true } : {}),
              },
            ]}
            onCreated={onCertMutated}
            onCancel={() => setAdding(false)}
          />
          </FormModal>
        )}
        {detail.kind === 'ready' && requesting && (
          // On a Device record the subject is the device itself — only device-scoped
          // frameworks apply, and the single device is pre-selected by the form.
          <FormModal title="Request certificate from vendor" titleId="request-cert-heading" onClose={() => setRequesting(false)} wide>
          <RequestCertificateForm
            frameworks={detail.frameworks.filter((f) => f.scope === 'device')}
            vendorId={v.id}
            devices={[
              {
                id: d.id,
                model: d.model,
                ...(d.version ? { version: d.version } : {}),
                // Story 8.9: the subject may still be this Company's own proposal.
                ...(d.proposedByCompanyId ? { proposed: true } : {}),
              },
            ]}
            onDone={() => setRequesting(false)}
            onCancel={() => setRequesting(false)}
          />
          </FormModal>
        )}
        {detail.kind === 'ready' && (
          <CertificateRows
            certificates={detail.certs}
            declared={detail.declared}
            frameworkCode={detail.frameworkCode}
            frameworkVerificationType={detail.frameworkVerificationType}
            manageEvidence
          />
        )}
      </section>

      <section className="card stack" aria-labelledby="device-sites-heading">
        <h2 id="device-sites-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Installed on</h2>
        {entry.sites.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Not installed on any Site.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {entry.sites.map((s) => (
              <li key={s.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
                {/* Drill down the spine to the installed-on Site (Story 4.4). */}
                <SpineLink label={s.name} hint={s.type} onClick={() => onOpenRecord?.({ kind: 'site', id: s.id })} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
